import {
    lazy,
    Suspense,
    useCallback,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import { CountrySelectorContext, type CountrySelectorContextValue } from '@/providers/country-selector-context';

const CountrySelectorDrawer = lazy(() =>
    import('@/components/country-selector/country-selector-drawer').then((module) => ({
        default: module.CountrySelectorDrawer,
    }))
);

export default function CountrySelectorProvider({ children }: PropsWithChildren): ReactElement {
    const [loaded, setLoaded] = useState(false);
    const [open, setOpen] = useState(false);
    const restoreFocusTargetRef = useRef<HTMLElement | null>(null);

    const openCountrySelector = useCallback(() => {
        const active = document.activeElement;
        restoreFocusTargetRef.current = active instanceof HTMLElement ? active : null;
        setLoaded(true);
        setOpen(true);
    }, []);

    const closeCountrySelector = useCallback(() => {
        setOpen(false);
    }, []);

    const value = useMemo<CountrySelectorContextValue>(
        () => ({
            open,
            openCountrySelector,
            closeCountrySelector,
            setOpen,
        }),
        [open, openCountrySelector, closeCountrySelector]
    );

    return (
        <CountrySelectorContext.Provider value={value}>
            {children}
            {loaded ? (
                <Suspense fallback={null}>
                    <CountrySelectorDrawer
                        open={open}
                        onOpenChange={setOpen}
                        restoreFocusTargetRef={restoreFocusTargetRef}
                    />
                </Suspense>
            ) : null}
        </CountrySelectorContext.Provider>
    );
}
